import { useState } from 'react';
import { C } from '../../lib/tokens.js';
import { downloadFile } from '../../lib/api.js';
import { IconButton } from '../../components/ui/Button.jsx';
import { PageSpinner } from '../../components/ui/Misc.jsx';
import { useViewport } from '../../lib/useViewport.js';

export function matches(term, ...fields) {
  if (!term) return true;
  const needle = term.trim().toLowerCase();
  return fields.some(f => String(f ?? '').toLowerCase().includes(needle));
}

export function exportCsv(rows, columns, filename) {
  const cols = columns.filter(c => c.label);
  const header = cols.map(c => JSON.stringify(c.label)).join(',');
  const body = rows.map(r =>
    cols.map(c => JSON.stringify(String(c.csvValue ? c.csvValue(r) : (r[c.key] ?? '')))).join(',')
  ).join('\n');
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(header + '\n' + body);
  a.download = filename;
  a.click();
}

export function SearchInput({ value, onChange, placeholder }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, width: '100%', maxWidth: 260, minWidth: 140, flex: '1 1 140px',
      background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 4, padding: '6px 10px',
    }}>
      <span className="material-icons" style={{ fontSize: 16, color: C.silver }}>search</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: C.fg, fontFamily: 'Roboto' }}
      />
      {value && (
        <span className="material-icons" style={{ fontSize: 14, color: C.silver, cursor: 'pointer' }}
          onClick={() => onChange('')}>close</span>
      )}
    </div>
  );
}

function TableRow({ cols, row, zebra, onClick }) {
  const [h, setH] = useState(false);
  return (
    <tr
      onClick={onClick ? () => onClick(row) : undefined}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ background: h ? C.rowHover : (zebra ? C.chrome : C.tin), cursor: onClick ? 'pointer' : 'default', borderBottom: `1px solid ${C.mercury}` }}>
      {cols.map(c => (
        <td key={c.key} style={{ padding: '10px 14px', fontSize: 13, textAlign: c.align || 'left', verticalAlign: 'middle' }}>
          {c.render ? c.render(row) : row[c.key]}
        </td>
      ))}
    </tr>
  );
}

function TableCard({ columns, row, onClick }) {
  const actionsCol = columns.find(c => c.key === 'actions');
  const mainCols = columns.filter(c => c.key !== 'actions');
  const [primaryCol, ...restCols] = mainCols;

  return (
    <div
      onClick={onClick ? () => onClick(row) : undefined}
      style={{
        background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 12,
        display: 'flex', flexDirection: 'column', gap: 8, cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
          {primaryCol && (primaryCol.render ? primaryCol.render(row) : row[primaryCol.key])}
        </div>
        {actionsCol && actionsCol.render(row)}
      </div>
      {restCols.map(c => (
        <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
          <span style={{ color: C.iron, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10, flex: 'none' }}>{c.label}</span>
          <span style={{ minWidth: 0, textAlign: 'right' }}>{c.render ? c.render(row) : row[c.key]}</span>
        </div>
      ))}
    </div>
  );
}

export function SimpleTable({ columns, rows, onRowClick, loading }) {
  const { isMobile } = useViewport();

  if (loading) {
    return (
      <div style={{ background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, overflow: 'auto' }}>
        <PageSpinner />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => <TableCard key={i} columns={columns} row={r} onClick={onRowClick} />)}
      </div>
    );
  }

  return (
    <div style={{ background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, overflow: 'auto' }}>
      <table style={{ width: '100%', minWidth: 480, borderCollapse: 'collapse' }}>
        <thead style={{ background: C.platinum }}>
          <tr style={{ borderBottom: `1px solid ${C.mercury}` }}>
            {columns.map(c => (
              <th key={c.key} style={{ textAlign: c.align || 'left', padding: '10px 14px', fontSize: 11, color: C.iron, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', width: c.width }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => <TableRow key={i} cols={columns} row={r} zebra={i % 2 === 1} onClick={onRowClick} />)}
        </tbody>
      </table>
    </div>
  );
}

// ─── Actions column shared between Orders and Invoices ────────────────────────
export function RowActions({ kind, id, no }) {
  const [progress, setProgress] = useState(null);
  const downloading = progress !== null;

  async function handleDownload(e) {
    e.stopPropagation();
    if (downloading) return;
    setProgress(true);
    try {
      await downloadFile(`/${kind}s/${id}/pdf`, `${no}.pdf`, setProgress);
    } catch {
      // download failed — button re-enables below so the user can retry
    } finally {
      setProgress(null);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
      <IconButton icon="download" title={downloading ? 'Downloading…' : 'Download PDF'}
        progress={downloading ? progress : undefined} disabled={downloading} onClick={handleDownload} />
    </div>
  );
}
